import { ITicker } from '@kattana-io/common';
import BigNumber from 'bignumber.js';
import { getPriceForPair } from '../helpers/statistics';
import { Direction } from '../hooks';

export const PRICE_FACTOR = 10 ** 4;
export const GWEI = 10 ** -9;

/**
 * Handle state of limit order proxy
 */
export enum LimitTabs {
  Available,
  NotAvailable,
  Deploying,
  NotSupported,
}

export enum LimitModes {
  Overline = 'OVERLINE',
  StopLimit = 'STOP_LIMIT',
}

export enum LimitTypes {
  LIMIT = 'LIMIT',
  GRID = 'GRID',
}

export interface ILimitGrid {
  initPrice: number;
  finalPrice: number;
  ordersNum: number;
}

interface ILimitState {
  limitTab: LimitTabs;
  baseBalance: string;
  quoteBalance: string;
  baseUSDBalance: string;
  quoteUSDBalance: string;
  currentPrice: number;
  stopPrice: number;
  limitPrice: number;
  direction: Direction;
  limitMode: LimitModes;
  proxyWallet?: string;
  isGrid: boolean;
  grid: ILimitGrid;
}

export enum LimitActions {
  SetLimitTab = 'LIMIT_SET_TAB',
  SetBaseBalance = 'LIMIT_SET_BASE',
  SetQuoteBalance = 'LIMIT_SET_QUOTE',
  SetBaseUSDBalance = 'LIMIT_SET_BASE_USD',
  SetQuoteUSDBalance = 'LIMIT_SET_QUOTE_USD',
  SetCurrentPrice = 'LIMIT_SET_CURRENT_PRICE',
  SetStopPrice = 'LIMIT_SET_STOP_PRICE',
  SetLimitPrice = 'LIMIT_SET_LIMIT_PRICE',
  SetDirection = 'LIMIT_SET_DIRECTION',
  SetLimitMode = 'LIMIT_SET_MODE',
  SetProxyWallet = 'LIMIT_SET_PROXY_WALLET',
  ToggleGrid = 'LIMIT_TOGGLE_GRID',
  SetGrid = 'LIMIT_SET_GRID',
  SetGridInitPrice = 'LIMIT_SET_GRID_INIT_PRICE',
  SetGridFinalPrice = 'LIMIT_SET_GRID_FINAL_PRICE',
  SetGridOrdersNum = 'LIMIT_SET_GRID_ORDERS_NUM',
}

export const getInitialLimitState = ({
  baseAmount,
  quoteAmount,
  meta,
}: {
  baseAmount: BigNumber;
  quoteAmount: BigNumber;
  meta?: ITicker;
}): ILimitState => {
  const currentPrice = getPriceForPair({ ticker: meta, quotePrice: true });
  const limitPrice = baseAmount.isGreaterThan(0)
    ? quoteAmount.div(baseAmount).toNumber()
    : currentPrice;

  return {
    limitTab: LimitTabs.Available,
    baseBalance: '0.0000',
    quoteBalance: '0.0000',
    baseUSDBalance: '0.0',
    quoteUSDBalance: '0.0',
    currentPrice,
    stopPrice: limitPrice,
    limitPrice,
    direction: Direction.BaseQuote,
    limitMode: LimitModes.StopLimit,
    isGrid: false,
    grid: {
      initPrice: currentPrice,
      finalPrice: currentPrice,
      ordersNum: 2,
    },
  };
};

export const limitReducer = (
  state: ILimitState,
  action: { payload?: any; type: LimitActions }
): ILimitState => {
  switch (action.type) {
  case LimitActions.SetLimitTab:
    return {
      ...state,
      limitTab: action.payload,
    };
  case LimitActions.SetBaseBalance:
    return {
      ...state,
      baseBalance: action.payload,
    };
  case LimitActions.SetQuoteBalance:
    return {
      ...state,
      quoteBalance: action.payload,
    };
  case LimitActions.SetBaseUSDBalance:
    return {
      ...state,
      baseUSDBalance: action.payload,
    };
  case LimitActions.SetQuoteUSDBalance:
    return {
      ...state,
      quoteUSDBalance: action.payload,
    };
  case LimitActions.SetCurrentPrice:
    return {
      ...state,
      currentPrice: action.payload,
    };
  case LimitActions.SetStopPrice:
    return {
      ...state,
      stopPrice: action.payload,
    };
  case LimitActions.SetLimitPrice:
    return {
      ...state,
      limitPrice: action.payload,
    };
  case LimitActions.SetDirection:
    return {
      ...state,
      direction: action.payload,
    };
  case LimitActions.SetLimitMode:
    return {
      ...state,
      limitMode: action.payload,
    };
  case LimitActions.SetProxyWallet:
    return {
      ...state,
      proxyWallet: action.payload,
    };
  case LimitActions.ToggleGrid:
    return {
      ...state,
      isGrid: !state.isGrid,
    };
  case LimitActions.SetGrid:
    return {
      ...state,
      grid: action.payload,
    };
  case LimitActions.SetGridInitPrice:
    return {
      ...state,
      grid: {
        ...state.grid,
        initPrice: action.payload,
      },
    };
  case LimitActions.SetGridFinalPrice:
    return {
      ...state,
      grid: {
        ...state.grid,
        finalPrice: action.payload,
      },
    };
  case LimitActions.SetGridOrdersNum:
    return {
      ...state,
      grid: {
        ...state.grid,
        ordersNum: action.payload,
      },
    };
  default:
    return state;
  }
};
